import type { CSSProperties } from 'react';
import classNames from 'classnames';
import { Card, Row, Col } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { DASHBOARD_PLOT_HEIGHT } from '../../utils/competition-chart';

export function Skeleton({
  className = '',
  style
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={classNames('placeholder d-block rounded', className)}
      style={style}
      aria-hidden
    />
  );
}

export function KpiCardsSkeleton({
  count = 4,
  cols = { xs: 6, lg: 3 }
}: {
  count?: number;
  cols?: { xs?: number; lg?: number };
}) {
  return (
    <Row className="g-3 placeholder-glow">
      {Array.from({ length: count }).map((_, i) => (
        <Col xs={cols.xs} lg={cols.lg} key={i}>
          <div className="rounded-3 border bg-white dark__bg-card-dark p-3 shadow-sm">
            <Skeleton style={{ height: '0.75rem', width: '5rem' }} />
            <Skeleton className="mt-3" style={{ height: '2rem', width: '4rem' }} />
            <Skeleton className="mt-2" style={{ height: '0.75rem', width: '7rem' }} />
          </div>
        </Col>
      ))}
    </Row>
  );
}

function CrmStatsCardSkeletonRow({ cols }: { cols: number }) {
  const lg = cols <= 1 ? 12 : 12 / Math.min(cols, 4);
  return (
    <Card className="placeholder-glow">
      <Card.Body>
        <Row>
          {Array.from({ length: cols }).map((_, index) => (
            <Col
              lg={lg}
              key={index}
              className={classNames({
                'border-bottom border-lg-0 border-lg-end': index !== cols - 1,
                'pb-3 pb-lg-0': index === 0,
                'py-3 py-lg-0': index > 0 && index < cols - 1,
                'pt-3 pt-lg-0': index === cols - 1 && cols > 1,
              })}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <Skeleton className="rounded-circle flex-shrink-0" style={{ width: '2rem', height: '2rem' }} />
                <Skeleton style={{ height: '0.75rem', width: '6rem' }} />
              </div>
              <div className="d-flex align-items-center gap-3" dir="ltr">
                <Skeleton style={{ height: '1.75rem', width: '3rem' }} />
                <Skeleton style={{ height: '1rem', width: '3.5rem' }} />
                <Skeleton className="flex-grow-1 ms-1 ps-2" style={{ height: '2rem' }} />
              </div>
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );
}

/** Matches Falcon `/dashboard/crm` CrmStats (one card, 3–4 columns). */
export function CrmStatsCardSkeleton({ count = 4 }: { count?: number }) {
  return <CrmStatsCardSkeletonRow cols={count} />;
}

export function SummaryCardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Row className="g-3 placeholder-glow">
      {Array.from({ length: count }).map((_, i) => (
        <Col xs={6} xl={3} key={i}>
          <div className="rounded-3 border bg-white dark__bg-card-dark p-3 shadow-sm">
            <Skeleton style={{ height: '0.75rem', width: '6rem' }} />
            <Skeleton className="mt-3" style={{ height: '2rem', width: '3rem' }} />
            <Skeleton className="mt-2" style={{ height: '0.75rem', width: '5rem' }} />
          </div>
        </Col>
      ))}
    </Row>
  );
}

export function ChartSectionSkeleton({ height = '14rem' }: { height?: string }) {
  return (
    <section className="rounded-3 border bg-white dark__bg-card-dark p-4 shadow-sm placeholder-glow">
      <Skeleton style={{ height: '1rem', width: '8rem' }} />
      <Skeleton className="mt-2" style={{ height: '0.75rem', width: '12rem' }} />
      <Skeleton className="mt-4 w-100" style={{ height }} />
    </section>
  );
}

export function BrandListRowSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="placeholder-glow">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="d-flex align-items-center gap-3 rounded-3 border bg-body-tertiary px-3 py-2 mb-2"
        >
          <Skeleton
            className="flex-shrink-0 rounded-3"
            style={{ height: '2.25rem', width: '2.25rem' }}
          />
          <div className="flex-grow-1 min-w-0">
            <Skeleton style={{ height: '1rem', width: '8rem' }} />
            <Skeleton className="mt-2" style={{ height: '0.75rem', width: '12rem' }} />
          </div>
          <Skeleton className="rounded-pill" style={{ height: '1.25rem', width: '2.5rem' }} />
          <Skeleton style={{ height: '1rem', width: '6rem' }} />
        </div>
      ))}
    </div>
  );
}

function SectionTitleSkeleton({
  titleWidth,
  subtitle = false,
  action = false,
}: {
  titleWidth: string;
  subtitle?: boolean;
  action?: boolean;
}) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 mb-3 mt-1">
      <div className="d-flex align-items-start gap-2">
        <Skeleton className="rounded-3 flex-shrink-0" style={{ width: '1.75rem', height: '1.75rem' }} />
        <div>
          <Skeleton style={{ height: '1.15rem', width: titleWidth }} />
          {subtitle ? <Skeleton className="mt-2" style={{ height: '0.7rem', width: '12rem' }} /> : null}
        </div>
      </div>
      {action ? <Skeleton style={{ height: '0.85rem', width: '5.5rem' }} /> : null}
    </div>
  );
}

function ChartCardHeaderSkeleton({
  titleWidth,
  subtitle = false,
  end,
}: {
  titleWidth: string;
  subtitle?: boolean;
  end?: 'icons' | 'select';
}) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-2 px-3 py-3 bg-body-tertiary border-bottom">
      <div>
        <Skeleton style={{ height: '1rem', width: titleWidth }} />
        {subtitle ? <Skeleton className="mt-2" style={{ height: '0.7rem', width: '9rem' }} /> : null}
      </div>
      {end === 'icons' ? (
        <div className="d-flex gap-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="rounded-2" style={{ width: 24, height: 24 }} />
          ))}
        </div>
      ) : null}
      {end === 'select' ? (
        <Skeleton className="rounded" style={{ height: '1.75rem', width: '9rem' }} />
      ) : null}
    </div>
  );
}

function LegendSkeleton({ count }: { count: number }) {
  return (
    <div className="mt-2 d-flex flex-wrap align-items-center justify-content-center gap-3 px-1" style={{ minHeight: '1.75rem' }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="d-flex align-items-center gap-2">
          <Skeleton className="rounded-1" style={{ width: '0.5rem', height: '0.5rem' }} />
          <Skeleton style={{ height: '0.7rem', width: i % 2 === 0 ? '3.5rem' : '2.75rem' }} />
        </div>
      ))}
    </div>
  );
}

function HorizontalBarsSkeleton({ rows = 4 }: { rows?: number }) {
  const widths = ['78%', '72%', '42%', '28%'];
  return (
    <div className="d-flex flex-column justify-content-center gap-3 w-100 px-1" style={{ height: DASHBOARD_PLOT_HEIGHT }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="d-flex align-items-center gap-2">
          <Skeleton className="flex-shrink-0" style={{ height: '0.7rem', width: '4.5rem' }} />
          <Skeleton className="rounded" style={{ height: '0.9rem', width: widths[i % widths.length] }} />
        </div>
      ))}
    </div>
  );
}

function VerticalBarsSkeleton() {
  const heights = ['55%', '88%', '80%', '48%'];
  return (
    <div className="d-flex align-items-end justify-content-around w-100 px-3 pb-3" style={{ height: DASHBOARD_PLOT_HEIGHT }}>
      {heights.map((height, i) => (
        <div key={i} className="d-flex flex-column align-items-center gap-2" style={{ width: '18%' }}>
          <div className="d-flex align-items-end justify-content-center gap-1 w-100" style={{ height: '85%' }}>
            <Skeleton className="rounded-top w-50" style={{ height }} />
            <Skeleton className="rounded-top w-50" style={{ height: `calc(${height} * 0.45)` }} />
          </div>
          <Skeleton style={{ height: '0.65rem', width: '100%' }} />
        </div>
      ))}
    </div>
  );
}

function DonutSkeleton() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center w-100" style={{ height: DASHBOARD_PLOT_HEIGHT }}>
      <Skeleton className="rounded-circle" style={{ width: '9.5rem', height: '9.5rem' }} />
      <Skeleton className="mt-3" style={{ height: '0.7rem', width: '5rem' }} />
      <Skeleton className="mt-2" style={{ height: '0.65rem', width: '3.5rem' }} />
    </div>
  );
}

function LineChartSkeleton() {
  return (
    <div className="w-100 d-flex flex-column justify-content-end gap-3 px-1" style={{ height: DASHBOARD_PLOT_HEIGHT }}>
      <div className="flex-grow-1 d-flex align-items-end">
        <Skeleton className="w-100 rounded-3" style={{ height: '72%' }} />
      </div>
      <div className="d-flex justify-content-between">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} style={{ height: '0.6rem', width: '1.75rem' }} />
        ))}
      </div>
    </div>
  );
}

function HeatmapSkeleton() {
  return (
    <div className="d-flex flex-column gap-2 w-100" style={{ height: DASHBOARD_PLOT_HEIGHT }}>
      <div className="d-flex gap-2 ps-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="flex-grow-1" style={{ height: '0.65rem' }} />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, row) => (
        <div key={row} className="d-flex align-items-center gap-2">
          <Skeleton className="flex-shrink-0" style={{ height: '0.7rem', width: '4.5rem' }} />
          {Array.from({ length: 4 }).map((_, col) => (
            <Skeleton key={col} className="flex-grow-1 rounded" style={{ height: '1.75rem' }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function CompetitorListSkeleton() {
  return (
    <ul className="list-unstyled mb-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="d-flex align-items-center justify-content-between gap-3 rounded-3 border bg-body-tertiary px-3 py-2 mb-2"
        >
          <div className="min-w-0 flex-grow-1">
            <Skeleton style={{ height: '0.85rem', width: '7rem' }} />
            <Skeleton className="mt-2" style={{ height: '0.65rem', width: '9rem' }} />
          </div>
          <Skeleton style={{ height: '0.85rem', width: '2.5rem' }} />
          <Skeleton style={{ height: '0.85rem', width: '3.5rem' }} />
        </li>
      ))}
    </ul>
  );
}

function ChartCardSkeleton({
  titleWidth,
  subtitle,
  end,
  plot,
  legendCount,
}: {
  titleWidth: string;
  subtitle?: boolean;
  end?: 'icons' | 'select';
  plot: 'bars-h' | 'bars-v' | 'donut' | 'line' | 'heat' | 'list';
  legendCount?: number;
}) {
  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      <ChartCardHeaderSkeleton titleWidth={titleWidth} subtitle={subtitle} end={end} />
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1 placeholder-glow">
        <div className="flex-grow-1 d-flex align-items-center">
          {plot === 'bars-h' ? <HorizontalBarsSkeleton /> : null}
          {plot === 'bars-v' ? <VerticalBarsSkeleton /> : null}
          {plot === 'donut' ? <DonutSkeleton /> : null}
          {plot === 'line' ? <LineChartSkeleton /> : null}
          {plot === 'heat' ? <HeatmapSkeleton /> : null}
          {plot === 'list' ? <CompetitorListSkeleton /> : null}
        </div>
        {legendCount ? <LegendSkeleton count={legendCount} /> : null}
      </Card.Body>
    </Card>
  );
}

export function DashboardSourcesBodySkeleton() {
  return (
    <div className="placeholder-glow">
      <Row className="mb-3 g-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Col xs={4} key={i}>
            <div className="rounded-3 border bg-body-tertiary px-3 py-2">
              <Skeleton style={{ height: '0.7rem', width: '5rem' }} />
              <Skeleton className="mt-2" style={{ height: '1.15rem', width: '2.5rem' }} />
            </div>
          </Col>
        ))}
      </Row>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="d-flex align-items-center gap-3 py-2">
          <Skeleton className="rounded-3 flex-shrink-0" style={{ width: '1.75rem', height: '1.75rem' }} />
          <Skeleton className="flex-grow-1" style={{ height: '0.85rem' }} />
          <Skeleton style={{ height: '0.7rem', width: '3rem' }} />
        </div>
      ))}
    </div>
  );
}

export function DashboardPageSkeleton() {
  return (
    <div className="placeholder-glow">
      <SectionTitleSkeleton titleWidth="5rem" />
      <Row className="g-3 mb-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Col xs={12} sm={6} md={6} lg key={i}>
            <Card className="h-100 overflow-hidden">
              <Card.Body>
                <Skeleton className="mb-3" style={{ height: '0.9rem', width: '6.5rem' }} />
                <Skeleton className="mb-2" style={{ height: '1.75rem', width: '4rem' }} />
                <Skeleton style={{ height: '0.7rem', width: '8.5rem' }} />
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <SectionTitleSkeleton titleWidth="8rem" subtitle action />
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <ChartCardSkeleton titleWidth="9rem" plot="bars-h" legendCount={4} />
        </Col>
        <Col lg={4}>
          <ChartCardSkeleton titleWidth="8rem" end="icons" plot="bars-v" legendCount={2} />
        </Col>
        <Col lg={4}>
          <ChartCardSkeleton titleWidth="9rem" end="select" plot="donut" legendCount={4} />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={6}>
          <ChartCardSkeleton titleWidth="5rem" subtitle plot="list" />
        </Col>
        <Col lg={6}>
          <ChartCardSkeleton titleWidth="8rem" subtitle plot="bars-h" legendCount={4} />
        </Col>
      </Row>
      <Row className="g-3 mb-4">
        <Col lg={6}>
          <ChartCardSkeleton titleWidth="8rem" subtitle plot="line" legendCount={4} />
        </Col>
        <Col lg={6}>
          <ChartCardSkeleton titleWidth="10rem" subtitle plot="heat" />
        </Col>
      </Row>

      <SectionTitleSkeleton titleWidth="11rem" subtitle action />
      <Card className="shadow-sm mb-4">
        <Card.Body className="pt-3">
          <div className="d-flex align-items-center gap-3 px-1 mb-2">
            <Skeleton style={{ height: '0.7rem', width: '4rem' }} />
            <Skeleton className="flex-grow-1" style={{ height: '0.7rem' }} />
            <Skeleton style={{ height: '0.7rem', width: '3.5rem' }} />
            <Skeleton style={{ height: '0.7rem', width: '3.5rem' }} />
            <Skeleton style={{ height: '0.7rem', width: '4rem' }} />
          </div>
          <BrandListRowSkeleton count={5} />
        </Card.Body>
      </Card>

      <SectionTitleSkeleton titleWidth="5rem" subtitle action />
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <DashboardSourcesBodySkeleton />
        </Card.Body>
      </Card>

      <SectionTitleSkeleton titleWidth="9rem" subtitle action />
      <AgGridSkeleton height="360px" />
    </div>
  );
}

export function PromptsPageSkeleton() {
  return (
    <>
      <div className="mb-4">
        <SummaryCardsSkeleton />
      </div>
      <AgGridSkeleton height="620px" />
    </>
  );
}

export function TopicsPageSkeleton() {
  return (
    <>
      <div className="mb-4">
        <SummaryCardsSkeleton />
      </div>
      <AgGridSkeleton height="500px" />
    </>
  );
}

export function VisibilityPageSkeleton() {
  return (
    <div className="d-flex flex-column gap-4">
      <KpiCardsSkeleton count={4} />
      <ChartSectionSkeleton height="16rem" />
      <AgGridSkeleton height="480px" />
      <Row className="g-4">
        <Col xl={6}>
          <AgGridSkeleton height="420px" />
        </Col>
        <Col xl={6}>
          <AgGridSkeleton height="420px" />
        </Col>
      </Row>
    </div>
  );
}

export function CompetitorsPageSkeleton() {
  return (
    <div className="d-flex flex-column gap-4">
      <KpiCardsSkeleton count={4} cols={{ xs: 6, lg: 3 }} />
      <AgGridSkeleton height="360px" />
      <AgGridSkeleton height="360px" />
      <AgGridSkeleton height="420px" />
      <AgGridSkeleton height="300px" />
    </div>
  );
}

export function SourcesPageSkeleton() {
  return (
    <>
      <div className="mb-4">
        <SummaryCardsSkeleton count={4} />
      </div>
      <Row className="g-3 mb-4">
        <Col xl={6}>
          <ChartCardSkeleton titleWidth="9rem" subtitle plot="line" legendCount={3} />
        </Col>
        <Col xl={6}>
          <ChartCardSkeleton titleWidth="6rem" subtitle plot="donut" legendCount={4} />
        </Col>
      </Row>
      <AgGridSkeleton height="520px" />
    </>
  );
}

export function SettingsPageSkeleton() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex flex-wrap gap-3 placeholder-glow">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="rounded-pill" style={{ height: '2rem', width: '6rem' }} />
        ))}
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <section key={i} className="rounded-3 border bg-white dark__bg-card-dark p-4 shadow-sm placeholder-glow">
          <Skeleton style={{ height: '1.25rem', width: '8rem' }} />
          <Skeleton className="mt-2" style={{ height: '0.75rem', width: '16rem' }} />
          <div className="mt-4 d-flex flex-column gap-3">
            <Skeleton className="rounded-3 w-100" style={{ height: '2.5rem' }} />
            <Skeleton className="rounded-3 w-100" style={{ height: '2.5rem' }} />
            <Skeleton className="rounded-3 w-100" style={{ height: '6rem' }} />
          </div>
        </section>
      ))}
    </div>
  );
}

export function PromptInspectorOverviewSkeleton() {
  return (
    <div className="d-flex flex-column gap-4 placeholder-glow">
      <div>
        <div className="mb-2 d-flex align-items-center justify-content-between">
          <Skeleton style={{ height: '0.75rem', width: '6rem' }} />
          <Skeleton style={{ height: '1.75rem', width: '3rem' }} />
        </div>
        <Skeleton className="rounded-pill w-100" style={{ height: '0.625rem' }} />
      </div>

      <ProviderComparisonSkeleton />

      <Row className="g-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Col xs={4} key={i}>
            <Skeleton className="rounded-3 w-100" style={{ height: '2.5rem' }} />
          </Col>
        ))}
      </Row>

      <div className="overflow-hidden rounded-3 border">
        <div className="d-flex align-items-center justify-content-between px-3 py-2">
          <div>
            <Skeleton style={{ height: '0.75rem', width: '5rem' }} />
            <Skeleton className="mt-2" style={{ height: '1rem', width: '12rem' }} />
          </div>
          <Skeleton style={{ height: '1rem', width: '1rem' }} />
        </div>
      </div>
    </div>
  );
}

export function PromptHistoryPanelSkeleton() {
  return (
    <div className="d-flex flex-column gap-4">
      <Row className="g-3 placeholder-glow">
        {Array.from({ length: 4 }).map((_, i) => (
          <Col xs={6} sm={3} key={i}>
            <div className="rounded-3 border bg-white dark__bg-card-dark px-3 py-2">
              <Skeleton style={{ height: '0.75rem', width: '5rem' }} />
              <Skeleton className="mt-3" style={{ height: '1.75rem', width: '3.5rem' }} />
              <Skeleton className="mt-2" style={{ height: '0.75rem', width: '6rem' }} />
            </div>
          </Col>
        ))}
      </Row>
      <ChartSectionSkeleton height="12rem" />
      <AgGridSkeleton height="320px" />
      <AgGridSkeleton height="240px" />
    </div>
  );
}

/** Matches the model-response card on the responses tab. */
export function PromptModelResponseSkeleton() {
  return (
    <Card className="overflow-hidden mb-0 placeholder-glow">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-2"
        title={
          <span className="d-inline-flex align-items-center gap-2">
            <Skeleton className="rounded-circle" style={{ width: '1.75rem', height: '1.75rem' }} />
            <Skeleton style={{ height: '0.85rem', width: '5rem' }} />
          </span>
        }
        endEl={
          <div className="d-flex gap-1">
            <Skeleton className="rounded-2" style={{ height: '1.75rem', width: '5.5rem' }} />
            <Skeleton className="rounded-2" style={{ height: '1.75rem', width: '5.5rem' }} />
          </div>
        }
      />
      <Card.Body className="pt-2 pb-3">
        <Skeleton className="mb-2" style={{ height: '0.65rem', width: '9rem' }} />
        <div className="rounded-3 border border-200 p-3 d-flex flex-column gap-2">
          <Skeleton className="w-100" style={{ height: '0.7rem' }} />
          <Skeleton className="w-100" style={{ height: '0.7rem' }} />
          <Skeleton style={{ height: '0.7rem', width: '94%' }} />
          <Skeleton style={{ height: '0.7rem', width: '88%' }} />
          <Skeleton style={{ height: '0.7rem', width: '72%' }} />
          <Skeleton style={{ height: '0.7rem', width: '61%' }} />
        </div>
        <div className="border-top border-200 mt-3 pt-3">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <Skeleton style={{ height: '0.8rem', width: '8.5rem' }} />
            <div className="d-flex gap-1">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  className="rounded-circle"
                  style={{ width: '1.35rem', height: '1.35rem' }}
                />
              ))}
            </div>
          </div>
          <div className="rounded-2 border border-200">
            <div className="d-flex gap-2 px-2 py-2 border-bottom">
              <Skeleton style={{ height: '0.65rem', width: '2rem' }} />
              <Skeleton className="flex-grow-1" style={{ height: '0.65rem', maxWidth: '5rem' }} />
              <Skeleton style={{ height: '0.65rem', width: '3rem' }} />
              <Skeleton style={{ height: '0.65rem', width: '4rem' }} />
              <Skeleton style={{ height: '0.65rem', width: '4rem' }} />
            </div>
            {Array.from({ length: 4 }).map((_, row) => (
              <div key={row} className="d-flex align-items-center gap-2 px-2 py-2 border-bottom border-200">
                <Skeleton className="rounded-circle flex-shrink-0" style={{ width: '1.25rem', height: '1.25rem' }} />
                <Skeleton className="flex-grow-1" style={{ height: '0.7rem', maxWidth: '6rem' }} />
                <Skeleton className="rounded-pill flex-shrink-0" style={{ height: '1rem', width: '4rem' }} />
                <Skeleton className="flex-shrink-0" style={{ height: '0.65rem', width: '2rem' }} />
                <Skeleton className="rounded-2 flex-shrink-0" style={{ height: '1.1rem', width: '4rem' }} />
              </div>
            ))}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export function ProviderComparisonSkeleton() {
  return <AgGridSkeleton height="280px" />;
}

export function OnboardingPageSkeleton() {
  return (
    <div className="w-50 mx-auto placeholder-glow" aria-hidden>
      <div className="mb-3">
        <Skeleton style={{ height: '1.75rem', width: '10rem' }} />
        <Skeleton className="mt-2" style={{ height: '0.75rem', width: '22rem', maxWidth: '100%' }} />
      </div>

      <Card className="theme-wizard mb-4">
        <Card.Header className="bg-body-tertiary">
          <div className="d-flex justify-content-center gap-5">
            {[0, 1, 2].map((step) => (
              <div key={step} className="d-flex flex-column align-items-center">
                <Skeleton className="rounded-circle" style={{ width: '2.5rem', height: '2.5rem' }} />
                <Skeleton className="mt-2 d-none d-md-block" style={{ height: '0.7rem', width: '3rem' }} />
              </div>
            ))}
          </div>
        </Card.Header>
      </Card>

      <Card className="mb-3 border">
        <Card.Body className="d-flex flex-column gap-3">
          <div>
            <Skeleton className="mb-1" style={{ height: '0.75rem', width: '9rem' }} />
            <div className="d-flex align-items-center gap-2">
              <Skeleton className="flex-grow-1 rounded" style={{ height: '38px' }} />
              <Skeleton className="rounded flex-shrink-0" style={{ width: 38, height: 38 }} />
            </div>
          </div>
          <div>
            <Skeleton className="mb-1" style={{ height: '0.75rem', width: '8rem' }} />
            <div className="rounded-3 border px-3 py-2 mb-2">
              <Skeleton style={{ height: '0.7rem', width: '100%' }} />
              <Skeleton className="mt-2" style={{ height: '0.7rem', width: '85%' }} />
              <Skeleton className="mt-2" style={{ height: '0.7rem', width: '60%' }} />
            </div>
            <div className="d-flex gap-2">
              <Skeleton className="flex-grow-1 rounded" style={{ height: '31px' }} />
              <Skeleton className="rounded flex-shrink-0" style={{ height: '31px', width: '4.5rem' }} />
            </div>
          </div>
        </Card.Body>
        <Card.Footer className="bg-body-tertiary d-flex justify-content-end">
          <Skeleton className="rounded" style={{ height: '2.25rem', width: '5rem' }} />
        </Card.Footer>
      </Card>
    </div>
  );
}
